/**
 * S13 — Tab "Funil" do painel People Memory.
 * KPIs últimos 30d a partir de syra_person_memory_metrics_daily (org-level).
 * Nota: a tabela é agregada por org+dia, não por grupo. Mostramos visão geral.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface Metric {
  candidates: number;
  awaiting: number;
  confirmed: number;
  rejected: number;
  archived: number;
  expired: number;
  guard_rejections: number;
  dm_sent: number;
  dm_responded: number;
  dm_rate: number;
  guard_rate: number;
}

export function PeopleMemoryFunnel({ groupId: _groupId }: { groupId: string }) {
  const { data: metrics, isLoading } = useQuery<Metric>({
    queryKey: ["pm-funnel"],
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      const { data } = await supabase
        .from("syra_person_memory_metrics_daily")
        .select("*")
        .gte("day", since);
      const rows = (data ?? []) as any[];
      const sum = (k: string) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
      const avg = (k: string) => rows.length ? rows.reduce((s, r) => s + (Number(r[k]) || 0), 0) / rows.length : 0;
      return {
        candidates: sum("candidates_total"),
        awaiting: sum("awaiting_total"),
        confirmed: sum("confirmed_total"),
        rejected: sum("rejected_total"),
        archived: sum("archived_total"),
        expired: sum("expired_total"),
        guard_rejections: sum("guard_rejection_total"),
        dm_sent: sum("dm_sent_total"),
        dm_responded: sum("dm_responded_total"),
        dm_rate: avg("dm_confirmation_rate"),
        guard_rate: avg("guard_rejection_rate"),
      };
    },
  });

  if (isLoading || !metrics) return <div className="text-muted-foreground p-4">Carregando…</div>;

  const guardActive = metrics.guard_rejections > 0;
  const dmRate = metrics.dm_sent > 0 ? metrics.dm_responded / metrics.dm_sent : 0;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        ℹ️ Métricas agregadas por organização (não por grupo). O grupo selecionado é usado nas outras tabs.
      </p>

      {!guardActive && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Guards aparentemente inativos</AlertTitle>
          <AlertDescription>0 rejeições de guard em 30d. Verifique se o pipeline está chamando o extractor.</AlertDescription>
        </Alert>
      )}
      {metrics.dm_sent > 0 && dmRate < 0.4 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Taxa de resposta de DM baixa ({Math.round(dmRate * 100)}%)</AlertTitle>
          <AlertDescription>O template de DM pode estar ineficaz. Considere revisão A/B.</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Candidatos" value={metrics.candidates} />
        <Stat label="Aguardando" value={metrics.awaiting} />
        <Stat label="Confirmados" value={metrics.confirmed} accent />
        <Stat label="Rejeitados" value={metrics.rejected} />
        <Stat label="Arquivados" value={metrics.archived} />
        <Stat label="Expirados" value={metrics.expired} />
        <Stat label="Guard rejections" value={metrics.guard_rejections} />
        <Stat label="DM enviados → respondidos" value={`${metrics.dm_sent} → ${metrics.dm_responded}`} />
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <Card className={`p-4 ${accent ? "border-primary" : ""}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold ${accent ? "text-primary" : ""}`}>{value}</div>
    </Card>
  );
}
